<script setup lang="ts">
/** CaField — label + hint + pesan error jadi satu, dengan hubungan a11y yang benar. */
const props = defineProps<{
  label: string
  hint?: string
  error?: string
  /** id kontrol di dalam slot; wajib supaya <label for> tersambung. */
  for?: string
}>()

const idHint = computed(() => (props.for ? `${props.for}-hint` : undefined))
const idError = computed(() => (props.for ? `${props.for}-error` : undefined))

/**
 * Satu id saja yang diumumkan pembaca layar, mengikuti apa yang benar-benar
 * tampil: saat ada galat, hint-nya memang digantikan galat di DOM — jadi
 * menunjuk id hint yang sudah tidak ada hanya membuat pengumumannya kosong.
 */
const dijelaskanOleh = computed(() => (props.error ? idError.value : idHint.value))
</script>

<template>
  <div class="flex flex-col gap-2">
    <label :for="props.for" class="text-sm font-semibold text-ink leading-snug">
      {{ label }}
    </label>

    <!-- Kontrol di dalam slot menerima hubungan a11y-nya lewat slot prop, bukan
         ditebak sendiri: `describedby` menunjuk hint ATAU galat yang sedang
         tampil, `invalid` menandai kolomnya salah isi. -->
    <slot :describedby="dijelaskanOleh" :invalid="Boolean(error)" />

    <!--
      role="alert" (implisit aria-live="assertive" + aria-atomic) supaya galat
      yang muncul SETELAH halaman dimuat benar-benar dibacakan. Tanpa ini,
      pengguna pembaca layar menekan "Tukar & buka alatnya", tidak mendengar
      apa pun, dan menyangka tombolnya rusak.
    -->
    <p
      v-if="error"
      :id="idError"
      role="alert"
      class="ca-caption text-bad flex items-start gap-1.5"
    >
      <UIcon name="i-lucide-circle-alert" class="size-4 shrink-0 mt-px" aria-hidden="true" />
      <span>{{ error }}</span>
    </p>
    <p v-else-if="hint" :id="idHint" class="ca-caption text-muted">{{ hint }}</p>
  </div>
</template>
